<template>
  <div class="bank-account-picker">
    <div class="subview-header">
      <button class="back-btn" @click="handleBack">
        <v-icon size="22">mdi-chevron-left</v-icon>
      </button>
      <div class="subview-title">{{ t('bankAccount') }}</div>
      <div class="spacer"></div>
    </div>
    
    <div class="subview-scroll">
      <IosSection form :title="t('accountNumber')">
        <div class="bank-account-row">
          <input
            v-model="prefix"
            type="text"
            inputmode="numeric"
            class="bank-input bank-input--prefix"
            :placeholder="t('accountPrefix')"
          />
          <span class="bank-separator">–</span>
          <input
            v-model="number"
            type="text"
            inputmode="numeric"
            class="bank-input bank-input--number"
            :placeholder="t('accountNumberPlaceholder')"
          />
        </div>
      </IosSection>

      <div v-if="error" class="bank-error">
        <v-icon size="16" color="error" class="mr-1">mdi-alert-circle</v-icon>
        {{ error }}
      </div>

      <IosSection form :title="t('bankCode')">
        <IosRow 
          :label="t('selectBank')"
          :value="selectedBankName"
          clickable
          first
          last
          @click="showPicker = true; searchQuery = ''"
        />
      </IosSection>

      <!-- Bank Picker -->
      <Transition name="slide">
        <div v-if="showPicker" class="subview" style="z-index: 10001;">
          <div class="subview-header">
            <button class="back-btn" @click="showPicker = false">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ t('selectBank') }}</div>
            <div class="spacer"></div>
          </div>

          <div class="subview-scroll">
            <IosSection form>
              <div class="search-input-wrapper-ios">
                <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
                <input 
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  :placeholder="t('searchBank')"
                />
              </div>
            </IosSection>

            <IosSection form v-if="filteredBanks.length > 0">
              <IosRow 
                v-for="(bank, index) in filteredBanks" 
                :key="bank.code"
                :label="bank.name"
                :subtitle="bank.code"
                :first="index === 0"
                :last="index === filteredBanks.length - 1"
                clickable
                :show-chevron="false"
                @click="selectBank(bank.code)"
              >
                <template #value>
                  <v-icon 
                    v-if="bankCode === bank.code" 
                    size="20" 
                    color="primary"
                  >mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>

            <div v-else class="search-empty">
              {{ t('noResults') }}
            </div>
          </div>
        </div>
      </Transition>

      <div v-if="prefix || number || bankCode" class="bank-remove-section">
        <v-btn
          variant="text"
          color="error"
          block
          @click="clear"
        >
          <v-icon start>mdi-delete-outline</v-icon>
          {{ t('removeBankAccount') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IosSection, IosRow } from '@/components/ios';

export interface BankAccountValue {
  prefix: string;
  number: string;
  bankCode: string;
}

const props = defineProps<{
  modelValue: BankAccountValue;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: BankAccountValue];
  'back': [];
}>();

const { t } = useI18n({ useScope: 'global' });

// Local state
const showPicker = ref(false);
const searchQuery = ref('');
const error = ref('');

// Two-way binding helpers
const prefix = computed({
  get: () => props.modelValue.prefix,
  set: (val: string) => {
    const sanitized = val.replace(/[^0-9]/g, '');
    emit('update:modelValue', { ...props.modelValue, prefix: sanitized });
  },
});

const number = computed({
  get: () => props.modelValue.number,
  set: (val: string) => {
    const sanitized = val.replace(/[^0-9]/g, '');
    emit('update:modelValue', { ...props.modelValue, number: sanitized });
  },
});

const bankCode = computed({
  get: () => props.modelValue.bankCode,
  set: (val: string) => {
    emit('update:modelValue', { ...props.modelValue, bankCode: val });
  },
});

// Czech banks
const czechBanks = [
  { code: '0100', name: 'Komerční banka' },
  { code: '0300', name: 'Československá obchodní banka' },
  { code: '0600', name: 'MONETA Money Bank' },
  { code: '0710', name: 'Česká národní banka' },
  { code: '0800', name: 'Česká spořitelna' },
  { code: '2010', name: 'Fio banka' },
  { code: '2020', name: 'Bank of China' },
  { code: '2030', name: 'AKCENTA' },
  { code: '2060', name: 'Citfin' },
  { code: '2070', name: 'Moravský Peněžní Ústav' },
  { code: '2100', name: 'Hypoteční banka' },
  { code: '2200', name: 'Citfin spořitelní družstvo' },
  { code: '2220', name: 'Artesa' },
  { code: '2240', name: 'Poštová banka' },
  { code: '2250', name: 'Banka CREDITAS' },
  { code: '2260', name: 'NEY spořitelní družstvo' },
  { code: '2275', name: 'Podnikatelská družstevní záložna' },
  { code: '2700', name: 'UniCredit Bank' },
  { code: '3030', name: 'Air Bank' },
  { code: '3050', name: 'BNP Paribas Personal Finance' },
  { code: '3060', name: 'PKO BP' },
  { code: '3500', name: 'ING Bank' },
  { code: '4000', name: 'Expobank' },
  { code: '4300', name: 'Národní rozvojová banka' },
  { code: '5500', name: 'Raiffeisenbank' },
  { code: '5800', name: 'J&T Banka' },
  { code: '6000', name: 'PPF banka' },
  { code: '6100', name: 'Equa bank' },
  { code: '6200', name: 'COMMERZBANK' },
  { code: '6210', name: 'mBank' },
  { code: '6300', name: 'BNP Paribas' },
  { code: '6700', name: 'Všeobecná úverová banka' },
  { code: '6800', name: 'Sberbank CZ' },
  { code: '7910', name: 'Deutsche Bank' },
  { code: '7940', name: 'Waldviertler Sparkasse Bank' },
  { code: '7950', name: 'Raiffeisen stavební spořitelna' },
  { code: '7960', name: 'ČSOB Stavební spořitelna' },
  { code: '7970', name: 'Wüstenrot' },
  { code: '7990', name: 'Modrá pyramida' },
  { code: '8030', name: 'Raiffeisenbank im Stiftland' },
  { code: '8040', name: 'Oberbank' },
  { code: '8060', name: 'Stavební spořitelna ČS' },
  { code: '8090', name: 'Česká exportní banka' },
  { code: '8150', name: 'HSBC Bank' },
  { code: '8200', name: 'PRIVAT BANK' },
  { code: '8220', name: 'Payment Execution' },
  { code: '8230', name: 'EEPAYS' },
  { code: '8240', name: 'Družstevní záložna Kredit' },
  { code: '8250', name: 'Bank of China (CEE)' },
  { code: '8260', name: 'Trinity Bank' },
  { code: '8270', name: 'Fairplay Pay' },
  { code: '8280', name: 'B-Efekt' },
  { code: '8290', name: 'Máš peníze' },
  { code: '8292', name: 'NovaPay' },
];

const filteredBanks = computed(() => {
  if (!searchQuery.value) return czechBanks;
  const q = searchQuery.value.toLowerCase();
  return czechBanks.filter(b => b.name.toLowerCase().includes(q) || b.code.includes(q));
});

const selectedBankName = computed(() => {
  if (!bankCode.value) return '';
  const bank = czechBanks.find(b => b.code === bankCode.value);
  return bank ? `${bank.code} – ${bank.name}` : bankCode.value;
});

const selectBank = (code: string) => {
  bankCode.value = code;
  showPicker.value = false;
};

// Checksum validation (mod-11)
const BANK_WEIGHTS = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1];

const validateBankPart = (part: string): boolean => {
  if (!part || part.length === 0) return true;
  if (part.length > 10) return false;
  const padded = part.padStart(10, '0');
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(padded[i], 10) * BANK_WEIGHTS[i];
  }
  return sum % 11 === 0;
};

const handleBack = () => {
  error.value = '';

  if (!prefix.value && !number.value && !bankCode.value) {
    emit('back');
    return;
  }

  if (prefix.value && !validateBankPart(prefix.value)) {
    error.value = t('bankAccountInvalidPrefix');
    return;
  }

  if (number.value && !validateBankPart(number.value)) {
    error.value = t('bankAccountInvalidNumber');
    return;
  }

  emit('back');
};

const clear = () => {
  emit('update:modelValue', { prefix: '', number: '', bankCode: '' });
  error.value = '';
  emit('back');
};

// Public helpers for parent components
const getDisplayValue = computed(() => {
  if (!number.value || !bankCode.value) return t('notSet');
  const lastDigits = number.value.slice(-3);
  return `...${lastDigits}/${bankCode.value}`;
});

defineExpose({ getDisplayValue });
</script>

<style lang="scss" scoped>
// Subview structural styles (self-contained, no parent dependency)
.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

// Search
.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
}

.search-icon {
  color: rgba(var(--v-theme-sheetText), 0.4);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.35);
  }
}

.search-empty {
  text-align: center;
  padding: 32px 16px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.95rem;
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

// Bank account inputs
.bank-account-row {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  gap: 0;
}

.bank-input {
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  padding: 12px 12px;
  border-radius: 12px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  transition: border-color 0.2s;
  
  &:focus {
    border-color: rgb(var(--v-theme-primary));
  }
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.35);
  }
  
  &--prefix {
    width: 90px;
    flex-shrink: 0;
    text-align: center;
  }
  
  &--number {
    flex: 1;
    min-width: 0;
  }
}

.bank-separator {
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 1.3rem;
  font-weight: 300;
  padding: 0 10px;
  flex-shrink: 0;
  user-select: none;
}

.bank-error {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  color: rgb(var(--v-theme-error));
  font-size: 0.85rem;
}

.bank-remove-section {
  padding: 24px 16px 0;
}
</style>
